import React, { useMemo, useState } from "react";
import "./SignatureApplicationModal.css";
import axios from "../../api/axios";
import NCALayerModal from "../../components/NCALayerModal";
import { ensureNCALayerConnected } from "../../utils/ensureNCALayer";

const SignatureApplicationModal = ({
  application,
  onClose,
  locale = "ru",
  finalAverage,
  summaryDecision,
  t,
  downloadFinalDecision,
  downloadArchiveAttachment,
  showSignatures = true,
  setShowSignatures = () => {},
  signatures = [],
  userId,
  pendingSignatures = [],
  token,
  onSignedSuccess,
  onSignedFail,
  refreshApplication,
  finalDocumentId: finalDocumentIdProp,
  isInternalExpert = false,
  isDepartmentHead = false,
}) => {
  const isKz = locale === "kz";

  const [ncaModalOpen, setNcaModalOpen] = useState(false);
  const pendingSignRef = React.useRef(false);

  const L = isKz
    ? {
        fileNotLoaded: "❌ PDF табылмады",
        noNCALayer: "NCALayer табылмады. Бағдарламаны іске қосып, қайталап көріңіз.",
        signError: "❌ ЭЦҚ арқылы қол қою қате орындалды",
        signSuccess: "✅ ЭЦҚ сәтті қойылды!",
        errorDefault: "❌ Қате орын алды",
        noFinalDocId: "❌ Қорытынды құжат ID табылмады",
        ncaNotFound: "❌ NCALayer табылмады немесе window.NCA жүктелмеген",
      }
    : {
        fileNotLoaded: "❌ PDF не найден",
        noNCALayer: "NCALayer не найден. Запустите приложение и повторите попытку.",
        signError: "❌ Подпись ЭЦП выполнена неправильно",
        signSuccess: "✅ ЭЦП успешно поставлена!",
        errorDefault: "❌ Произошла ошибка",
        noFinalDocId: "❌ Не найден ID итогового документа",
        ncaNotFound: "❌ NCALayer не найден или window.NCA не загружен",
      };

  const localizeServerMessage = (msg) => {
    let m = String(msg || "");
    m = m.replace(/^❌\s*❌\s*/g, "❌ ");
    if (
      m.includes("қойылмаған") ||
      m.includes("номер") ||
      m.includes("қол қою кезеңіне дайын емес")
    ) {
      return isKz
        ? "❌ Бұйрық/құжат нөмірі қойылмаған немесе қол қою кезеңіне дайын емес"
        : "❌ Номер приказа/документа не назначен или документ не подготовлен к подписи";
    }
    return m || L.errorDefault;
  };

  const [banner, setBanner] = useState(null);
  const [busySign, setBusySign] = useState(false);
  const [localShowSig, setLocalShowSig] = useState(true);
  const [iSigned, setISigned] = useState(false);

  const showBanner = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 3500);
  };

const allSigners = signatures?.length ? signatures : (application?.signers ?? []);

const currentUserSignature = useMemo(
  () => allSigners.find((s) => s.user_id === userId && !s.signed_at && !s.rejected_at),
  [allSigners, userId]
);

const currentUserSigned = useMemo(
  () => allSigners.find((s) => s.user_id === userId && !!s.signed_at),
  [allSigners, userId]
);

  if (!application) return null;

  const appId = application?.application_id ?? application?.id ?? null;

  const finalDocumentId =
    finalDocumentIdProp ??
    application?.document_id ??
    application?.final_document_id ??
    application?.finalDocumentId ??
    application?.final_document?.id ??
    null;

  const endpoints = finalDocumentId
    ? {
        pdfBase64: `/final-documents/${finalDocumentId}/base64`,
        sign: `/final-documents/sign/${finalDocumentId}`,
      }
    : null;

  const handleSignWithECP = async () => {
    if (!finalDocumentId || !endpoints) {
      alert(L.noFinalDocId);
      return;
    }

    // window.NCA бар ма тексеру
    if (!window.NCA || typeof window.NCA.createCMSSignatureFromBase64 !== "function") {
      alert(L.ncaNotFound);
      return;
    }

    // NCALayer қосылған ба тексеру
    const ok = await ensureNCALayerConnected({ connectTimeoutMs: 1200 });
    if (!ok) {
      pendingSignRef.current = true;
      setNcaModalOpen(true);
      return;
    }

    setBusySign(true);

    try {
      // 1) PDF base64 алу
      const pdfRes = await axios.get(endpoints.pdfBase64, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });

      const base64File =
        pdfRes?.data?.base64 ||
        pdfRes?.data?.file_base64 ||
        pdfRes?.data?.data;

      if (!base64File) {
        alert(L.fileNotLoaded);
        setBusySign(false);
        return;
      }

      // 2) NCALayer арқылы CMS қол қою
      window.blockScreen?.();

      // ✅ window.NCA.createCMSSignatureFromBase64 — дұрыс шақыру
      window.NCA.createCMSSignatureFromBase64(
        "PKCS12",
        "SIGNATURE",
        base64File,
        true,
        async (result) => {
          window.unblockScreen?.();

          const code = result?.getCode ? result.getCode() : result?.code;
          const isSuccess = String(code) === "200";

          if (!isSuccess) {
            alert(L.signError);
            setBusySign(false);
            return;
          }

          const signedPdfBase64 = result?.getResponseObject
            ? result.getResponseObject()
            : result?.responseObject;

          // 3) Серверге жіберу
          try {
            await axios.post(
              endpoints.sign,
              { signed_pdf_base64: signedPdfBase64 },
              {
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            // 4) Сәтті
            setISigned(true);
            showBanner("success", L.signSuccess);

            try {
              await refreshApplication?.();
            } catch {}

            onSignedSuccess?.();
            setBusySign(false);
          } catch (err) {
            const msg = localizeServerMessage(
              err?.response?.data?.message || err?.message
            );
            alert(`❌ ${msg}`);
            onSignedFail?.(msg);
            setBusySign(false);
          }
        }
      );
    } catch (err) {
      window.unblockScreen?.();
      const msg = localizeServerMessage(
        err?.response?.data?.message || err?.message
      );
      alert(`❌ ${msg}`);
      onSignedFail?.(msg);
      setBusySign(false);
    }
  };

  const appData = application.application_data ?? {};
  const infoRows = [
    [isKz ? "Оқу басылымы" : "Учебное издание", appData.title ?? application.title ?? "—", ""],
    [isKz ? "Баспа" : "Издательство", appData.publisher ?? application.publisher ?? "—", ""],
    [isKz ? "Сынып" : "Класс", appData.grade ?? application.grade ?? "—", ""],
    [isKz ? "Санат" : "Категория", appData.category ?? application.category ?? "—", ""],
    [isKz ? "Авторлар" : "Авторы", appData.authors ?? application.authors ?? "—", ""],
    [t.average, (application.average_score ?? finalAverage) ?? "—", ""],
    [t.decision, summaryDecision ?? application.verdict ?? "—", ""],
  ];

  return (
    <div
      className="sam-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sam-title"
      onClick={(e) => e.target.classList.contains("sam-modal") && onClose?.()}
    >
      <div className="sam-backdrop" />
      <div className="sam-dialog" role="document">
        <header className="sam-header">
          <h3 id="sam-title" className="sam-title">
            {isKz ? "Өтініш туралы мәлімет" : "Информация о заявке"}
          </h3>
          <button
            type="button"
            className="sam-close"
            aria-label={isKz ? "Жабу" : "Закрыть"}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        {banner && (
          <div
            className={`sam-banner sam-banner--${banner.type === "success" ? "ok" : "err"}`}
            role="status"
          >
            {banner.text}
          </div>
        )}

        <div className="sam-body">
          <table className="sam-table">
            <tbody>
              {infoRows.map(([label, value, icon], idx) => (
                <tr key={idx} className="sam-row">
                  <td className="sam-cell sam-cell--label">
                    <span className="sam-ico" aria-hidden="true">{icon}</span> {label}
                  </td>
                  <td className="sam-cell sam-cell--value">{String(value)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="sam-btnrow">
            {currentUserSignature && !iSigned && (
              <button
                type="button"
                className="sam-btn sam-btn--primary"
                onClick={busySign ? undefined : handleSignWithECP}
                disabled={busySign}
              >
                {busySign ? (
                  <>
                    <span className="sam-spinner" />
                    {isKz ? "Қол қойылуда..." : "Подписывается..."}
                  </>
                ) : (
                  isKz ? "Қол қою (ЭЦҚ)" : "Подписать (ЭЦП)"
                )}
              </button>
            )}

            {(iSigned || currentUserSigned) && (
              <div className="sam-signed-badge">
                ✅ {isKz ? "Сіз қол қойдыңыз" : "Вы уже подписали"}
              </div>
            )}

            <button
  type="button"
  className="sam-btn sam-btn--outline"
  onClick={() => {
    if (!finalDocumentId) return alert(L.noFinalDocId);
    downloadFinalDecision(finalDocumentId);
  }}
>
  {isKz ? "Қорытындыны жүктеу" : "Скачать заключение"}
</button>

            {(isInternalExpert || isDepartmentHead) && (
  <button
    type="button"
    className="sam-btn sam-btn--outline"
    onClick={() => {
      if (!finalDocumentId) return alert(L.noFinalDocId);
      if (!downloadArchiveAttachment) {
        return alert(
          isKz ? "Жүктеу функциясы берілмеген" : "Функция скачивания не передана"
        );
      }
      downloadArchiveAttachment(finalDocumentId);
    }}
  >
    {isKz ? "Архивке кететін құжатты жүктеу" : "Скачать для архива"}
  </button>
)}
          </div>

         <div className="sam-toggle">
  <button
    type="button"
    className="sam-btn sam-btn--ghost"
    onClick={() => setLocalShowSig((v) => !v)}
  >
    {localShowSig
      ? (isKz ? "Қолтаңбаларды жабу ▲" : "Скрыть подписи ▲")
      : (isKz ? "Қолтаңбаларды көру ▼" : "Показать подписи ▼")
    }
  </button>
</div>

{localShowSig && (
            <section className="sam-signatures">
              <table className="sam-sigtable">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{isKz ? "Сарапшы" : "Эксперт"}</th>
<th></th>
                    <th>{isKz ? "Қол қойылу статусы" : "Статус подписи"}</th>
                    <th>{isKz ? "Әрекет" : "Действие"}</th>
                  </tr>
                </thead>
                <tbody>
                  {allSigners.map((s, idx) => {
  const isMe = userId === s.user_id;
  const isNotSigned = !s.signed_at && !s.rejected_at;
  return (
    <tr key={s.id} className={isMe ? "sam-me" : undefined}>
      <td>{idx + 1}</td>
      <td>{isKz ? `Сарапшы ${idx + 1}` : `Эксперт ${idx + 1}`}</td>
      <td>—</td>
      <td>
        {s.signed_at ? "✅" : s.rejected_at ? "❌" : "—"}
      </td>
      <td className="sam-sigaction">
        {isMe && isNotSigned && (
          <span className="sam-hint">
            {isKz ? "Қол қою түймесі жоғарыда" : "Кнопка подписания выше"}
          </span>
        )}
      </td>
    </tr>
  );
})}
                </tbody>
              </table>
            </section>
          )}
        </div>

        <footer className="sam-footer">
          <button
            type="button"
            className="sam-btn sam-btn--ghost"
            onClick={onClose}
          >
            {isKz ? "Жабу" : "Закрыть"}
          </button>
        </footer>
      </div>

      <NCALayerModal
        open={ncaModalOpen}
        locale={locale}
        onClose={() => {
          setNcaModalOpen(false);
          pendingSignRef.current = false;
        }}
        onRetry={async () => {
          const ok = await ensureNCALayerConnected({ connectTimeoutMs: 1200 });
          if (!ok) return;

          setNcaModalOpen(false);

          if (pendingSignRef.current) {
            pendingSignRef.current = false;
            handleSignWithECP();
          }
        }}
      />
    </div>
  );
};

export default SignatureApplicationModal;